import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Calendar,
  DollarSign,
  MapPin,
  Building,
  FileText,
  Info,
  Calculator,
  ArrowLeft,
  RefreshCw,
  Activity
} from 'lucide-react';
import { masterDataAPI } from '../../services/master-data-api.service';
import { worksApiService } from '../../services/workApi.service';
import {
  Work,
  CreateWorkDto,
  UpdateWorkDto,
  GrantLevel,
  GrantType,
  WorkStage,
  CategoryPST,
  FieldOptions,
  ValidationRules
} from '../../types/work.types';
import { WorkTypeEntity, Contractor } from '../../types/master-data.types';
import { District,Mandal } from '../../types/territorial.types';
import { getAuthHeaders } from '../../utils/authHeaders';

// Stages-of-Work master API base
const STAGES_API_BASE =
  process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';

// ==========================================
// FORM FIELD COMPONENTS
// ==========================================

interface FormFieldProps {
  label: string;
  name: string;
  type?: 'text' | 'number' | 'date' | 'select' | 'textarea' | 'combo';
  value: any;
  onChange: (value: any) => void;
  options?: Array<{ value: string | number; label: string }>;
  required?: boolean;
  error?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  rows?: number;
  help?: string;
  disabled?: boolean;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  options,
  required,
  error,
  placeholder,
  min,
  max,
  step,
  rows = 3,
  help,
  disabled = false
}) => {
  const baseInputClasses = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${
    error ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
  } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`;

  const renderInput = () => {
    switch (type) {
      case 'select':
        return (
          <select
            className={baseInputClasses}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          >
            <option value="">Select {label}</option>
            {options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'combo':
        return (
          <>
            <input
              type="text"
              list={`${name}-list`}
              className={baseInputClasses}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              autoComplete="off"
            />
            <datalist id={`${name}-list`}>
              {options?.map((option) => (
                <option key={option.value} value={String(option.value)}>
                  {option.label}
                </option>
              ))}
            </datalist>
          </>
        );

      case 'textarea':
        return (
          <textarea
            className={baseInputClasses}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            className={baseInputClasses}
            value={value || ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            className={baseInputClasses}
            value={value ? (typeof value === 'string' ? value.split('T')[0] : value) : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
        );

      default:
        return (
          <input
            type="text"
            className={baseInputClasses}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
          />
        );
    }
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {renderInput()}
      {help && (
        <p className="text-xs text-gray-500 flex items-center">
          <Info className="w-3 h-3 mr-1" />
          {help}
        </p>
      )}
      {error && (
        <p className="text-xs text-red-600 flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" />
          {error}
        </p>
      )}
    </div>
  );
};

// ==========================================
// FORM SECTIONS
// ==========================================

const BasicDetailsSection: React.FC<{
  formData: Partial<Work>;
  errors: Record<string, string>;
  onChange: (field: string, value: any) => void;
  fieldOptions: FieldOptions | null;
  workTypeOptions: Array<{ value: string | number; label: string }>;
}> = ({ formData, errors, onChange, fieldOptions, workTypeOptions }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <FileText className="w-5 h-5 mr-2 text-blue-600" />
        Basic Work Details
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <FormField
          label="Serial Number (SNO)"
          name="sno"
          type="number"
          value={formData.sno}
          onChange={(value) => onChange('sno', value)}
          required
          error={errors.sno}
          placeholder="e.g., 1001"
          help="Unique identifier for the work"
        />
        
        <FormField
          label="OAS Grant Level"
          name="oas_grant_level"
          type="select"
          value={formData.oas_grant_level}
          onChange={(value) => onChange('oas_grant_level', value)}
          options={fieldOptions?.grantLevels.map(level => ({ value: level, label: level })) || []}
          required
          error={errors.oas_grant_level}
        />
        
        <FormField
          label="Grant Type"
          name="grant_type"
          type="select"
          value={formData.grant_type}
          onChange={(value) => onChange('grant_type', value)}
          options={fieldOptions?.grantTypes.map(type => ({ value: type, label: type })) || []}
          required
          error={errors.grant_type}
        />
        
        <FormField
          label="Work Type"
          name="work_type"
          type="select"
          value={formData.work_type}
          onChange={(value) => onChange('work_type', value)}
          options={workTypeOptions}
          required
          error={errors.work_type}
        />
        
        <FormField
          label="Work Sub Type"
          name="work_sub_type"
          type="text"
          value={formData.work_sub_type}
          onChange={(value) => onChange('work_sub_type', value)}
          error={errors.work_sub_type}
          placeholder="e.g., Upgradation to BT Standard"
        />
        
        <FormField
          label="Category (P/S/T)"
          name="category_pst"
          type="select"
          value={formData.category_pst}
          onChange={(value) => onChange('category_pst', value)}
          options={[
            { value: CategoryPST.PLAIN, label: 'Plain (P)' },
            { value: CategoryPST.SCP, label: 'Scheduled Caste Plan (S)' },
            { value: CategoryPST.TSP, label: 'Tribal Sub Plan (T)' }
          ]}
          error={errors.category_pst}
        />
      </div>
      
      <div className="mt-6">
        <FormField
          label="Work Name"
          name="work_name"
          type="textarea"
          value={formData.work_name}
          onChange={(value) => onChange('work_name', value)}
          required
          error={errors.work_name}
          placeholder="Enter complete work name and description..."
          rows={3}
          help="Provide a detailed description of the work including location and scope"
        />
      </div>
    </div>
  );
};

const LocationDetailsSection: React.FC<{
  formData: Partial<Work>;
  errors: Record<string, string>;
  onChange: (field: string, value: any) => void;
  districtOptions: Array<{ value: string | number; label: string }>;
  mandalOptions: Array<{ value: string | number; label: string }>;
  constituencyOptions: Array<{ value: string | number; label: string }>;
}> = ({ formData, errors, onChange, districtOptions, mandalOptions, constituencyOptions }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <MapPin className="w-5 h-5 mr-2 text-green-600" />
        Location Details
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <FormField
          label="District"
          name="district_id"
          type="select"
          value={formData.district_id}
          onChange={(value) => onChange('district_id', value)}
          options={districtOptions}
          required
          error={errors.district_id}
          placeholder="Select district"
          help="Select the district where work is located"
        />
        
        <FormField
          label="Mandal"
          name="mandal_id"
          type="select"
          value={formData.mandal_id}
          onChange={(value) => onChange('mandal_id', value)}
          options={mandalOptions}
          required
          error={errors.mandal_id}
          placeholder="Select mandal"
          help="Select the mandal within the district"
        />
        
        <FormField
          label="Assembly Constituency"
          name="constituency_id"
          type="select"
          value={formData.constituency_id}
          onChange={(value) => onChange('constituency_id', value)}
          options={constituencyOptions}
          error={errors.constituency_id}
          placeholder="Select constituency"
          help="Assembly Constituency this work falls under"
        />

        <FormField
          label="Length Sanctioned (KM)"
          name="length_sanctioned_km"
          type="number"
          value={formData.length_sanctioned_km}
          onChange={(value) => onChange('length_sanctioned_km', value)}
          error={errors.length_sanctioned_km}
          placeholder="0.00"
          step={0.001}
          min={0}
          help="Total sanctioned length in kilometers"
        />
      </div>
    </div>
  );
};

const FinancialDetailsSection: React.FC<{
  formData: Partial<Work>;
  errors: Record<string, string>;
  onChange: (field: string, value: any) => void;
}> = ({ formData, errors, onChange }) => {
  const calculatedValues = useMemo(() => {
    const lengthUpto2023 = Number(formData.length_completed_upto_31_03_2023) || 0;
    const lengthDuringFY = Number(formData.length_completed_during_fy_23_24) || 0;
    const expUpto2023 = Number(formData.total_exp_upto_31_03_2023) || 0;
    const expDuringFY = Number(formData.exp_during_fy_23_24) || 0;
    const sanctionedLength = Number(formData.length_sanctioned_km) || 0;
    const estimateCost = Number(formData.estimate_cost) || 0;
    const rasAmount = Number(formData.ras_amount) || 0;

    return {
      cumulativeLength: lengthUpto2023 + lengthDuringFY,
      cumulativeExpenditure: expUpto2023 + expDuringFY,
      balanceLength: sanctionedLength - lengthUpto2023,
      balanceCost: (rasAmount || estimateCost) - expUpto2023
    };
  }, [
    formData.length_completed_upto_31_03_2023,
    formData.length_completed_during_fy_23_24,
    formData.total_exp_upto_31_03_2023,
    formData.exp_during_fy_23_24,
    formData.length_sanctioned_km,
    formData.estimate_cost,
    formData.ras_amount
  ]);

  return (
    <div className="space-y-6">
      {/* Cost Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <DollarSign className="w-5 h-5 mr-2 text-green-600" />
          Financial Details
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FormField
            label="Estimate Cost (₹)"
            name="estimate_cost"
            type="number"
            value={formData.estimate_cost}
            onChange={(value) => onChange('estimate_cost', value)}
            required
            error={errors.estimate_cost}
            placeholder="0"
            min={0}
            step={0.01}
            help="Original estimated cost of the work"
          />
          
          <FormField
            label="RAS Amount (₹)"
            name="ras_amount"
            type="number"
            value={formData.ras_amount}
            onChange={(value) => onChange('ras_amount', value)}
            error={errors.ras_amount}
            placeholder="0"
            min={0}
            step={0.01}
            help="Revised Administrative Sanction amount (if applicable)"
          />
          
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2 flex items-center">
              <Calculator className="w-4 h-4 mr-1" />
              Calculated Values
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-700">Sanctioned Amount:</span>
                <span className="font-semibold text-blue-900">
                  {worksApiService.formatCurrency(formData.ras_amount || formData.estimate_cost || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">Escalation:</span>
                <span className={`font-semibold ${
                  (formData.ras_amount || 0) > (formData.estimate_cost || 0) ? 'text-red-600' : 'text-green-600'
                }`}>
                  {formData.ras_amount && formData.estimate_cost ? 
                    `${(((formData.ras_amount - formData.estimate_cost) / formData.estimate_cost) * 100).toFixed(1)}%` : '0%'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Tracking */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <Calculator className="w-5 h-5 mr-2 text-purple-600" />
          Progress Tracking
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Physical Progress */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4 text-center bg-blue-50 py-2 rounded-lg">
              Physical Progress (Length in KM)
            </h4>
            <div className="space-y-4">
              <FormField
                label="Length Completed up to 31.03.2023"
                name="length_completed_upto_31_03_2023"
                type="number"
                value={formData.length_completed_upto_31_03_2023}
                onChange={(value) => onChange('length_completed_upto_31_03_2023', value)}
                error={errors.length_completed_upto_31_03_2023}
                placeholder="0.000"
                min={0}
                step={0.001}
              />
              
              <FormField
                label="Length Completed during FY 2023-24"
                name="length_completed_during_fy_23_24"
                type="number"
                value={formData.length_completed_during_fy_23_24}
                onChange={(value) => onChange('length_completed_during_fy_23_24', value)}
                error={errors.length_completed_during_fy_23_24}
                placeholder="0.000"
                min={0}
                step={0.001}
              />
              
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-700">Cumulative Completed:</span>
                    <span className="font-semibold text-blue-900">
                      {calculatedValues.cumulativeLength.toFixed(3)} km
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">Balance Length:</span>
                    <span className="font-semibold text-blue-900">
                      {calculatedValues.balanceLength.toFixed(3)} km
                    </span>
                  </div>
                  {formData.length_sanctioned_km && (
                    <div className="flex justify-between">
                      <span className="text-blue-700">Progress:</span>
                      <span className="font-semibold text-blue-900">
                        {((calculatedValues.cumulativeLength / formData.length_sanctioned_km) * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Financial Progress */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4 text-center bg-green-50 py-2 rounded-lg">
              Financial Progress (Amount in ₹)
            </h4>
            <div className="space-y-4">
              <FormField
                label="Total Expenditure up to 31.03.2023"
                name="total_exp_upto_31_03_2023"
                type="number"
                value={formData.total_exp_upto_31_03_2023}
                onChange={(value) => onChange('total_exp_upto_31_03_2023', value)}
                error={errors.total_exp_upto_31_03_2023}
                placeholder="0"
                min={0}
                step={0.01}
              />
              
              <FormField
                label="Expenditure during FY 2023-24"
                name="exp_during_fy_23_24"
                type="number"
                value={formData.exp_during_fy_23_24}
                onChange={(value) => onChange('exp_during_fy_23_24', value)}
                error={errors.exp_during_fy_23_24}
                placeholder="0"
                min={0}
                step={0.01}
              />
              
              <div className="bg-green-50 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-700">Cumulative Expenditure:</span>
                    <span className="font-semibold text-green-900">
                      {worksApiService.formatCurrency(calculatedValues.cumulativeExpenditure)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Balance Cost:</span>
                    <span className="font-semibold text-green-900">
                      {worksApiService.formatCurrency(calculatedValues.balanceCost)}
                    </span>
                  </div>
                  {(formData.ras_amount || formData.estimate_cost) && (
                    <div className="flex justify-between">
                      <span className="text-green-700">Utilization:</span>
                      <span className="font-semibold text-green-900">
                        {((calculatedValues.cumulativeExpenditure / (formData.ras_amount || formData.estimate_cost || 1)) * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Bills */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
          Pending Bills & Outstanding Work
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Bills Pending with PAO (₹)"
            name="bills_pending_pao_bro_loc"
            type="number"
            value={formData.bills_pending_pao_bro_loc}
            onChange={(value) => onChange('bills_pending_pao_bro_loc', value)}
            error={errors.bills_pending_pao_bro_loc}
            placeholder="0"
            min={0}
            step={0.01}
            help="Bills pending with PAO for want of BRO/LOC"
          />
          
          <FormField
            label="Work Done but Not Recorded (₹)"
            name="work_done_not_recorded_bills"
            type="number"
            value={formData.work_done_not_recorded_bills}
            onChange={(value) => onChange('work_done_not_recorded_bills', value)}
            error={errors.work_done_not_recorded_bills}
            placeholder="0"
            min={0}
            step={0.01}
            help="Work done but bills not submitted to PAO"
          />
        </div>
      </div>
    </div>
  );
};

const AgencyDetailsSection: React.FC<{
  formData: Partial<Work>;
  errors: Record<string, string>;
  onChange: (field: string, value: any) => void;
  contractorOptions: Array<{ value: string | number; label: string }>;
  agencyOptions: Array<{ value: string | number; label: string }>;
}> = ({ formData, errors, onChange, contractorOptions, agencyOptions }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <Building className="w-5 h-5 mr-2 text-indigo-600" />
        Agency & Contract Details
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <FormField
          label="Agency Name"
          name="agency_name"
          type="combo"
          value={formData.agency_name}
          onChange={(value) => onChange('agency_name', value)}
          options={agencyOptions}
          required
          error={errors.agency_name}
          placeholder="Select or type executing agency"
          help="Pick a registered vendor/agency or type a new one"
        />
        
        <FormField
          label="Technical Person (TP)"
          name="tp"
          type="text"
          value={formData.tp}
          onChange={(value) => onChange('tp', value)}
          error={errors.tp}
          placeholder="Technical person designation"
        />
        
        <FormField
          label="Contractor"
          name="contractor_id"
          type="select"
          value={formData.contractor_id}
          onChange={(value) => onChange('contractor_id', value)}
          options={contractorOptions}
          error={errors.contractor_id}
          placeholder="Select contractor"
          help="Contractor assigned to this work"
        />
        
        <FormField
          label="Agreement Date"
          name="agreement_date"
          type="date"
          value={formData.agreement_date}
          onChange={(value) => onChange('agreement_date', value)}
          error={errors.agreement_date}
          help="Date of agreement signing"
        />
        
        <FormField
          label="Completion Date Set"
          name="completion_date_set"
          type="date"
          value={formData.completion_date_set}
          onChange={(value) => onChange('completion_date_set', value)}
          error={errors.completion_date_set}
          help="Scheduled completion date"
        />
      </div>
    </div>
  );
};

const OfficialDetailsSection: React.FC<{
  formData: Partial<Work>;
  errors: Record<string, string>;
  onChange: (field: string, value: any) => void;
}> = ({ formData, errors, onChange }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <FileText className="w-5 h-5 mr-2 text-red-600" />
        Official Proceedings & Sanctions
      </h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h4 className="font-medium text-gray-900 mb-4 text-center bg-blue-50 py-2 rounded-lg">
            Original Administrative Sanction (OAS)
          </h4>
          <div className="space-y-4">
            <FormField
              label="OAS Proceedings Number"
              name="oas_proceedings_number"
              type="text"
              value={formData.oas_proceedings_number}
              onChange={(value) => onChange('oas_proceedings_number', value)}
              required
              error={errors.oas_proceedings_number}
              placeholder="OAS proceedings or GO number"
            />
            
            <FormField
              label="OAS Sanction Date"
              name="oas_sanction_date"
              type="date"
              value={formData.oas_sanction_date}
              onChange={(value) => onChange('oas_sanction_date', value)}
              required
              error={errors.oas_sanction_date}
              help="Date of original administrative sanction"
            />
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-4 text-center bg-green-50 py-2 rounded-lg">
            Revised Administrative Sanction (RAS)
          </h4>
          <div className="space-y-4">
            <FormField
              label="RAS Proceedings Number"
              name="ras_proceedings_number"
              type="text"
              value={formData.ras_proceedings_number}
              onChange={(value) => onChange('ras_proceedings_number', value)}
              error={errors.ras_proceedings_number}
              placeholder="RAS proceedings or GO number"
              help="Only if RAS has been accorded"
            />
            
            <FormField
              label="RAS Sanction Date"
              name="ras_sanction_date"
              type="date"
              value={formData.ras_sanction_date}
              onChange={(value) => onChange('ras_sanction_date', value)}
              error={errors.ras_sanction_date}
              help="Date of revised administrative sanction"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusDetailsSection: React.FC<{
  formData: Partial<Work>;
  errors: Record<string, string>;
  onChange: (field: string, value: any) => void;
  fieldOptions: FieldOptions | null;
  abstractStageOptions: Array<{ value: string; label: string }>;
  subStageOptions: Array<{ value: string; label: string }>;
  detailedStageOptions: Array<{ value: string; label: string }>;
}> = ({ formData, errors, onChange, fieldOptions, abstractStageOptions, subStageOptions, detailedStageOptions }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <Activity className="w-5 h-5 mr-2 text-purple-600" />
        Work Status & Stage
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Abstract Stage of Work"
          name="abstract_stage_of_work"
          type="select"
          value={formData.abstract_stage_of_work}
          onChange={(value) => onChange('abstract_stage_of_work', value)}
          options={abstractStageOptions.length ? abstractStageOptions : (fieldOptions?.workStages.map(stage => ({ value: stage, label: stage })) || [])}
          required
          error={errors.abstract_stage_of_work}
        />

        <FormField
          label="Sub Stage of Work"
          name="sub_stage_of_work"
          type="select"
          value={formData.sub_stage_of_work}
          onChange={(value) => onChange('sub_stage_of_work', value)}
          options={subStageOptions.length ? subStageOptions : (fieldOptions?.subStages.map(stage => ({ value: stage, label: stage })) || [])}
          error={errors.sub_stage_of_work}
          help="Stage gates from the Stages-of-Work master (drives dashboard tiles)"
        />
      </div>

      <div className="mt-6">
        {detailedStageOptions.length > 0 ? (
          <FormField
            label="Detailed Stage of Work"
            name="detailed_stage_of_work"
            type="select"
            value={formData.detailed_stage_of_work}
            onChange={(value) => onChange('detailed_stage_of_work', value)}
            options={detailedStageOptions}
            error={errors.detailed_stage_of_work}
            help="Execution stages defined for the selected work type"
          />
        ) : (
          <FormField
            label="Detailed Stage Description"
            name="detailed_stage_of_work"
            type="textarea"
            value={formData.detailed_stage_of_work}
            onChange={(value) => onChange('detailed_stage_of_work', value)}
            error={errors.detailed_stage_of_work}
            placeholder="Provide detailed description of current work stage..."
            rows={3}
            help="Select a work type to load its stages, or describe the current stage"
          />
        )}
      </div>
    </div>
  );
};

// ==========================================
// MAIN WORK FORM COMPONENT
// ==========================================

interface WorkFormProps {
  workId?: number;
  mode: 'create' | 'edit';
  onSave?: (work: Work) => void;
  onCancel?: () => void;
}

const WorkForm: React.FC<WorkFormProps> = ({
  workId,
  mode,
  onSave,
  onCancel
}) => {
  // When rendered via the /works/edit/:id route, take the id from the URL.
  const routeParams = useParams<{ id?: string }>();
  const effectiveWorkId = workId ?? (routeParams.id ? Number(routeParams.id) : undefined);
  const [formData, setFormData] = useState<Partial<Work>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldOptions, setFieldOptions] = useState<FieldOptions | null>(null);
  const [validationRules, setValidationRules] = useState<ValidationRules | null>(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [districts, setDistricts] = useState<District[]>([]);
  const [mandals, setMandals] = useState<Mandal[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkTypeEntity[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [stageMaster, setStageMaster] = useState<any[]>([]);
  const [constituencies, setConstituencies] = useState<any[]>([]);

  const sections = [
    { id: 'basic', label: 'Basic Details', icon: FileText },
    { id: 'location', label: 'Location', icon: MapPin },
    { id: 'financial', label: 'Financial', icon: DollarSign },
    { id: 'agency', label: 'Agency & Contract', icon: Building },
    { id: 'official', label: 'Official Proceedings', icon: FileText },
    { id: 'status', label: 'Status & Progress', icon: Activity }
  ];

  // Map API data to FormField options
  const districtOptions = useMemo(() => districts.map(district => ({
    value: district.id,
    label: district.name
  })), [districts]);

  const mandalOptions = useMemo(() => mandals.map(mandal => ({
    value: mandal.id,
    label: mandal.name
  })), [mandals]);

  const constituencyOptions = useMemo(() => constituencies.map(c => ({
    value: c.id,
    label: c.code ? `${c.name} (${c.code})` : c.name
  })), [constituencies]);

  const workTypeOptions = useMemo(() => workTypes.map(workType => ({
    value: workType.work_type_name, // Using work_type_name as it matches Work.work_type (string)
    label: workType.work_type_name
  })), [workTypes]);

  const contractorOptions = useMemo(() => contractors.map(contractor => ({
    value: contractor.contractor_id,
    label: `${contractor.contractor_name} (${contractor.firm_name})`
  })), [contractors]);

  // Agency suggestions sourced from the vendor/agency master (firm name preferred,
  // else contractor name). De-duplicated; agency_name stays a free-text string.
  const agencyOptions = useMemo(() => {
    const names = new Set<string>();
    contractors.forEach((c) => {
      const n = (c.firm_name || c.contractor_name || '').trim();
      if (n) names.add(n);
    });
    return Array.from(names).sort().map((n) => ({ value: n, label: n }));
  }, [contractors]);

  // Stage dropdowns driven by the Stages-of-Work master.
  // work_type is stored by name on the form, so resolve its id for DETAILED stages.
  const selectedWorkTypeId = useMemo(
    () => workTypes.find(w => w.work_type_name === formData.work_type)?.work_type_id,
    [workTypes, formData.work_type]
  );
  const toOpts = (rows: any[]) =>
    rows.sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0))
      .map(s => ({ value: s.name, label: s.name }));
  const abstractStageOptions = useMemo(
    () => toOpts(stageMaster.filter(s => s.stage_level === 'ABSTRACT')),
    [stageMaster]
  );
  const subStageOptions = useMemo(
    () => toOpts(stageMaster.filter(s => s.stage_level === 'SUB')),
    [stageMaster]
  );
  const detailedStageOptions = useMemo(
    () => toOpts(stageMaster.filter(s => s.stage_level === 'DETAILED' && s.work_type_id === selectedWorkTypeId)),
    [stageMaster, selectedWorkTypeId]
  );

  // ==========================================
  // INITIALIZATION
  // ==========================================

  const initializeForm = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch field options, validation rules, districts, work types, and contractors
      const [options, rules, districtsData, workTypesData, contractorsData] = await Promise.all([
        worksApiService.getFieldOptions(),
        worksApiService.getValidationRules(),
        masterDataAPI.getDistricts(),
        masterDataAPI.getWorkTypes(),
        masterDataAPI.getContractors()
      ]);
      
      setFieldOptions(options);
      setValidationRules(rules);
      setDistricts(districtsData);
      setWorkTypes(workTypesData);
      setContractors(contractorsData);

      // Stages-of-Work master (ABSTRACT/SUB generic + DETAILED per work type)
      try {
        const stagesResp = await fetch(`${STAGES_API_BASE}/work-stages/active`, {
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        });
        setStageMaster(stagesResp.ok ? await stagesResp.json() : []);
      } catch {
        setStageMaster([]);
      }

      // Constituency master (Assembly Constituency drill-down)
      try {
        const cResp = await fetch(`${STAGES_API_BASE}/constituencies/active`, {
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        });
        const cJson = cResp.ok ? await cResp.json() : [];
        setConstituencies(Array.isArray(cJson) ? cJson : (cJson?.data ?? []));
      } catch {
        setConstituencies([]);
      }

      // If editing, fetch existing work data and corresponding mandals
      if (mode === 'edit' && effectiveWorkId) {
        const work = await worksApiService.getWorkById(effectiveWorkId);
        setFormData(work);
        if (work.district_id) {
          const mandalsData = await masterDataAPI.getMandalsByDistrict(work.district_id);
          setMandals(mandalsData);
        }
      } else {
        // Initialize with default values for create mode
        setFormData({
          abstract_stage_of_work: WorkStage.NOT_STARTED,
          oas_grant_level: GrantLevel.CENTRAL,
          length_completed_upto_31_03_2023: 0,
          length_completed_during_fy_23_24: 0,
          total_exp_upto_31_03_2023: 0,
          exp_during_fy_23_24: 0,
          bills_pending_pao_bro_loc: 0,
          work_done_not_recorded_bills: 0,
          is_active: true
        });
      }
    } catch (err) {
      setErrors({ general: err instanceof Error ? err.message : 'Failed to initialize form' });
    } finally {
      setLoading(false);
    }
  }, [mode, effectiveWorkId]);

  useEffect(() => {
    initializeForm();
  }, [initializeForm]);

  // Fetch mandals when district_id changes
  useEffect(() => {
    const fetchMandals = async () => {
      if (formData.district_id) {
        try {
          const mandalsData = await masterDataAPI.getMandalsByDistrict(Number(formData.district_id));
          setMandals(mandalsData);
        } catch (err) {
          setErrors({ general: 'Failed to fetch mandals' });
        }
      } else {
        setMandals([]); // Clear mandals if no district is selected
      }
    };
    fetchMandals();
  }, [formData.district_id]);

  // ==========================================
  // FORM HANDLERS
  // ==========================================

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Clear mandal_id if district_id changes
    if (field === 'district_id') {
      setFormData(prev => ({ ...prev, mandal_id: undefined }));
    }
  }, [errors]);

  const validateForm = useCallback(async (): Promise<boolean> => {
    const newErrors: Record<string, string> = {};

    // Required field validation
    if (!formData.sno) newErrors.sno = 'Serial Number is required';
    if (!formData.work_name || formData.work_name.trim().length < 10) {
      newErrors.work_name = 'Work name must be at least 10 characters long';
    }
    if (!formData.estimate_cost || formData.estimate_cost <= 0) {
      newErrors.estimate_cost = 'Estimate cost must be greater than zero';
    }
    if (!formData.oas_grant_level) newErrors.oas_grant_level = 'Grant level is required';
    if (!formData.grant_type) newErrors.grant_type = 'Grant type is required';
    if (!formData.district_id) newErrors.district_id = 'District is required';
    if (!formData.mandal_id) newErrors.mandal_id = 'Mandal is required';
    if (!formData.agency_name) newErrors.agency_name = 'Agency name is required';
    if (!formData.oas_proceedings_number) newErrors.oas_proceedings_number = 'OAS proceedings number is required';
    if (!formData.oas_sanction_date) newErrors.oas_sanction_date = 'OAS sanction date is required';
    if (!formData.work_type) {
      newErrors.work_type = 'Work type is required';
    } else if (mode === 'create' && !workTypes.find(w => w.work_type_name === formData.work_type)) {
      // Backend requires work_type_id; ensure the selected name maps to a master row.
      newErrors.work_type = 'Selected work type is not recognised. Please pick from the list.';
    }
    if (!formData.abstract_stage_of_work) newErrors.abstract_stage_of_work = 'Work stage is required';

    // Business rule validation
    if (formData.ras_amount && formData.estimate_cost && formData.ras_amount < formData.estimate_cost) {
      newErrors.ras_amount = 'RAS amount cannot be less than estimate cost';
    }

    if (formData.cumulative_length_completed && formData.length_sanctioned_km && 
        formData.cumulative_length_completed > formData.length_sanctioned_km) {
      newErrors.cumulative_length_completed = 'Cumulative length cannot exceed sanctioned length';
    }

    // Date validation
    if (formData.agreement_date && formData.oas_sanction_date) {
      if (new Date(formData.agreement_date) < new Date(formData.oas_sanction_date)) {
        newErrors.agreement_date = 'Agreement date cannot be before OAS sanction date';
      }
    }

    if (formData.completion_date_set && formData.agreement_date) {
      if (new Date(formData.completion_date_set) < new Date(formData.agreement_date)) {
        newErrors.completion_date_set = 'Completion date cannot be before agreement date';
      }
    }

    if (formData.ras_sanction_date && formData.oas_sanction_date) {
      if (new Date(formData.ras_sanction_date) < new Date(formData.oas_sanction_date)) {
        newErrors.ras_sanction_date = 'RAS sanction date cannot be before OAS sanction date';
      }
    }

    // Validate district-mandal combination
    if (formData.district_id && formData.mandal_id) {
      try {
        const validation = await masterDataAPI.validateDistrictMandalCombination?.(
          Number(formData.district_id),
          Number(formData.mandal_id)
        );
        if (validation && !validation.valid) {
          newErrors.mandal_id = 'Invalid district-mandal combination';
        }
      } catch (err) {
        newErrors.mandal_id = 'Failed to validate district-mandal combination';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, mode, workTypes]);

  // Build the create payload that matches the backend CreateWorkDto whitelist:
  // resolves work_type_id from the selected name, folds the per-FY entries into
  // the cumulative snapshot works_master stores, and drops any field the backend
  // would reject (forbidNonWhitelisted). The granular per-FY split fields are
  // intentionally NOT sent here — they belong to the work_progress ledger.
  const buildCreatePayload = useCallback((): CreateWorkDto => {
    const num = (v: any): number | undefined => {
      if (v === undefined || v === null || v === '') return undefined;
      const n = Number(v);
      return Number.isNaN(n) ? undefined : n;
    };

    const workTypeId = workTypes.find(w => w.work_type_name === formData.work_type)?.work_type_id;

    const lengthUpto = Number(formData.length_completed_upto_31_03_2023) || 0;
    const lengthDuring = Number(formData.length_completed_during_fy_23_24) || 0;
    const expUpto = Number(formData.total_exp_upto_31_03_2023) || 0;
    const expDuring = Number(formData.exp_during_fy_23_24) || 0;
    const cumulativeLength = lengthUpto + lengthDuring;
    const cumulativeExpenditure = expUpto + expDuring;

    const payload: CreateWorkDto = {
      sno: Number(formData.sno),
      oas_grant_level: formData.oas_grant_level as GrantLevel,
      grant_type: formData.grant_type as string,
      district_id: Number(formData.district_id),
      mandal_id: Number(formData.mandal_id),
      work_name: formData.work_name as string,
      oas_proceedings_number: formData.oas_proceedings_number as string,
      oas_sanction_date: formData.oas_sanction_date as string,
      work_type_id: Number(workTypeId),
      work_type: formData.work_type,
      work_sub_type: formData.work_sub_type,
      length_sanctioned_km: num(formData.length_sanctioned_km),
      estimate_cost: Number(formData.estimate_cost),
      agency_name: formData.agency_name as string,
      tp: formData.tp,
      agreement_date: formData.agreement_date,
      completion_date_set: formData.completion_date_set,
      ras_amount: num(formData.ras_amount),
      ras_proceedings_number: formData.ras_proceedings_number,
      ras_sanction_date: formData.ras_sanction_date,
      abstract_stage_of_work: formData.abstract_stage_of_work as WorkStage,
      sub_stage_of_work: formData.sub_stage_of_work,
      detailed_stage_of_work: formData.detailed_stage_of_work,
      category_pst: formData.category_pst,
      contractor_id: num(formData.contractor_id),
      constituency_id: num(formData.constituency_id),
      cumulative_length_completed: cumulativeLength || undefined,
      cum_exp_on_work: cumulativeExpenditure || undefined,
      bills_pending_pao_bro_loc: num(formData.bills_pending_pao_bro_loc),
      work_done_not_recorded_bills: num(formData.work_done_not_recorded_bills),
    };

    (Object.keys(payload) as (keyof CreateWorkDto)[]).forEach(k => {
      if (payload[k] === undefined) delete payload[k];
    });
    return payload;
  }, [formData, workTypes]);

  const handleSubmit = useCallback(async () => {
    try {
      setSaving(true);
      
      const isValid = await validateForm();
      if (!isValid) {
        return;
      }

      let savedWork: Work;

      if (mode === 'create') {
        savedWork = await worksApiService.createWork(buildCreatePayload());
      } else {
        savedWork = await worksApiService.updateWork(effectiveWorkId!, formData as UpdateWorkDto);
      }

      onSave?.(savedWork);
    } catch (err) {
      setErrors({
        general: err instanceof Error ? err.message : `Failed to ${mode} work`
      });
    } finally {
      setSaving(false);
    }
  }, [formData, mode, effectiveWorkId, validateForm, onSave, buildCreatePayload]);

  const handleNext = useCallback(() => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  }, [currentSection, sections.length]);

  const handlePrevious = useCallback(() => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  }, [currentSection]);

  // ==========================================
  // RENDER LOADING STATE
  // ==========================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER MAIN FORM
  // ==========================================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={onCancel}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {mode === 'create' ? 'Create New Work' : 'Edit Work'}
                </h1>
                <p className="text-gray-600 mt-1">
                  {mode === 'create' 
                    ? 'Fill in all required fields to create a new work'
                    : `Editing work ${formData.sno ? `SNO: ${formData.sno}` : ''}`
                  }
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{saving ? 'Saving...' : 'Save Work'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Banner */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-800">{errors.general}</span>
              <button
                onClick={() => setErrors(prev => ({ ...prev, general: '' }))}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Section Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Form Sections</h3>
              <span className="text-sm text-gray-600">
                Section {currentSection + 1} of {sections.length}
              </span>
            </div>
            
            <div className="flex space-x-2 overflow-x-auto">
              {sections.map((section, index) => {
                const Icon = section.icon;
                const isActive = index === currentSection;
                const isCompleted = index < currentSection;
                
                return (
                  <button
                    key={section.id}
                    onClick={() => setCurrentSection(index)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : isCompleted
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                    <span>{section.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="space-y-6">
          {currentSection === 0 && (
            <BasicDetailsSection
              formData={formData}
              errors={errors}
              onChange={handleFieldChange}
              fieldOptions={fieldOptions}
              workTypeOptions={workTypeOptions}
            />
          )}
          
          {currentSection === 1 && (
            <LocationDetailsSection
              formData={formData}
              errors={errors}
              onChange={handleFieldChange}
              districtOptions={districtOptions}
              mandalOptions={mandalOptions}
              constituencyOptions={constituencyOptions}
            />
          )}
          
          {currentSection === 2 && (
            <FinancialDetailsSection
              formData={formData}
              errors={errors}
              onChange={handleFieldChange}
            />
          )}
          
          {currentSection === 3 && (
            <AgencyDetailsSection
              agencyOptions={agencyOptions}
              formData={formData}
              errors={errors}
              onChange={handleFieldChange}
              contractorOptions={contractorOptions}
            />
          )}
          
          {currentSection === 4 && (
            <OfficialDetailsSection
              formData={formData}
              errors={errors}
              onChange={handleFieldChange}
            />
          )}
          
          {currentSection === 5 && (
            <StatusDetailsSection
              formData={formData}
              errors={errors}
              onChange={handleFieldChange}
              fieldOptions={fieldOptions}
              abstractStageOptions={abstractStageOptions}
              subStageOptions={subStageOptions}
              detailedStageOptions={detailedStageOptions}
            />
          )}
        </div>

        {/* Navigation Footer */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8">
          <div className="flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={currentSection === 0}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <div className="flex items-center space-x-2">
              {sections.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentSection
                      ? 'bg-blue-600'
                      : index < currentSection
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            
            {currentSection === sections.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{saving ? 'Saving...' : `${mode === 'create' ? 'Create' : 'Update'} Work`}</span>
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkForm;